import "react-datepicker/dist/react-datepicker.css";
import classes from "./DriverInfo.module.css";
import DatePicker from "react-datepicker";
import ModalWrapper from "../modalWrapper/ModalWrapper";
import { BaseTable } from "../UI/baseTable/BaseTable";
import { StatusChip } from "../statusChip/StatusChip";
import { LocationIcon } from "../../images/drivers/locationIcon";
import { CalendarIcon } from "../../images/drivers/calendarIcon";
import { SelectElement } from "../selectElement/SelectElement";
import { fetchCustomers } from "../../redux/customer/thunks/getCustomerThunk";
import { getStatusClass } from "../../helperFunctions/getStatusClass";
import { ResetFiltersBtn } from "../buttons/resetFiltersBtn/ResetFiltersBtn";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { statusTranslations } from "../../helperFunctions/statuses";
import { useEffect, useState } from "react";
import { fetchDeliveryHistory } from "../../redux/package/thunks/getDeliveryHistoryThunk";
import { useSelector, useDispatch } from "react-redux";
import {
  toggleDriverInfoModal,
  setIsDriverRoutes,
  setIsDriverLocationFromRoutes,
  setIsDriverLocationShown,
  setIsModalsBgShown,
  setSelectedDriverLocationIndex,
} from "../../redux/globalFunctions/globalFnSlice";

export function DriverInfo() {
  const dispatch = useDispatch();
  const { t } = useTranslations();
  const modalText = t.driverInfoModal;

  const { isDriverRoutes, selectedDriverId, language } = useSelector(
    (state) => state.global
  );

  const { customers } = useSelector((state) => state.customers);
  const drivers = useSelector((state) => state.drivers.drivers);
  const currentDeliveriesState = useSelector(
    (state) => state.currentDeliveries
  );
  const historyDeliveriesState = useSelector((state) => state.deliveryHistory);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");

  // Fetch customers once
  useEffect(() => {
    dispatch(fetchCustomers({ per_page: 100 }));
  }, [dispatch]);

  const selectedDriver = drivers.find(
    (driver) => driver.u_id === selectedDriverId
  );

  const driverName = selectedDriver
    ? `${selectedDriver.u_firstname} ${selectedDriver.u_lastname}`
    : "";

  const headerText = isDriverRoutes
    ? modalText.routes.headerText
    : modalText.history.headerText;

  const displayText = `${headerText} ${driverName}`;

  function handleCloseModal() {
    dispatch(toggleDriverInfoModal());
    dispatch(setIsDriverRoutes(false));
  }

  // Format date for API
  const formatDate = (date, endOfDay = false) => {
    if (!date) return null;
    const formattedDate = new Date(date);
    formattedDate.setHours(
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      0
    );
    const year = formattedDate.getFullYear();
    const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
    const day = String(formattedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T${
      formattedDate.toTimeString().split(" ")[0]
    }.000Z`;
  };

  // Fetch data when filters change
  useEffect(() => {
    if (!isDriverRoutes) {
      fetchData();
    }
  }, [isDriverRoutes, fromDate, toDate, selectedAddress]);

  const fetchData = () => {
    const filters = {
      driver_id: selectedDriverId,
      from_date: formatDate(fromDate),
      to_date: formatDate(toDate, true),
      address: selectedAddress || null,
    };
    dispatch(fetchDeliveryHistory(filters));
  };

  function handleMapIconClick(index) {
    dispatch(toggleDriverInfoModal());
    dispatch(setIsModalsBgShown(true));
    dispatch(setIsDriverLocationShown(true));
    dispatch(setSelectedDriverLocationIndex(index));
    if (!isDriverRoutes) dispatch(setIsDriverLocationFromRoutes(true));
  }

  const handleAddressChange = (e) => {
    const address = e.target.value;
    setSelectedAddress(address === "default" ? "" : address);
  };

  const resetFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedAddress("");
  };

  const isResetButtonDisabled = !fromDate && !toDate && !selectedAddress;

  // Select data and status depending on mode
  const { deliveries, status } = isDriverRoutes
    ? {
        deliveries:
          currentDeliveriesState.status === "succeeded"
            ? currentDeliveriesState.deliveries
            : [],
        status: currentDeliveriesState.status,
      }
    : {
        deliveries:
          historyDeliveriesState.status === "succeeded"
            ? historyDeliveriesState.deliveries
            : [],
        status: historyDeliveriesState.status,
      };

  // Table columns
  const columns = [
    { label: modalText.table.order },
    { label: modalText.table.date },
    { label: modalText.table.address },
    { label: modalText.table.status },
    { label: modalText.table.map },
  ];

  // Table rows
  const rows =
    deliveries &&
    deliveries?.map((item, index) => [
      item.p_id,
      new Date(item.p_order_date).toLocaleDateString(),
      item.p_dropoff_address_name,
      isDriverRoutes ? (
        <div className={getStatusClass(item.p_status)}>
          {statusTranslations[language]?.[item.p_status?.toLowerCase()] ||
            item.p_status}
        </div>
      ) : (
        <StatusChip status={item.p_status} />
      ),
      <LocationIcon onClick={() => handleMapIconClick(index)} />,
    ]);

  // Filters content (only for history)
  const filters = !isDriverRoutes && (
    <div className={classes.driverHistoryFilters}>
      {modalText.history.filters.filterBy}
      <div>
        <SelectElement
          name="address"
          id="address"
          placeholder={modalText.history.filters.address}
          value={selectedAddress || ""}
          onChange={handleAddressChange}
          options={[...new Set(customers?.map((address) => address))]}
          optionValueKey="c_lid"
          optionLabelKey="c_address"
          width="250px"
        />
      </div>
      <div className={classes.dateFilter}>
        <CalendarIcon styles={{ cursor: "default" }} />
        <DatePicker
          selected={fromDate}
          onChange={setFromDate}
          selectsStart
          startDate={fromDate}
          endDate={toDate}
          placeholderText={modalText.fromDate}
          dateFormat="MM/dd/yyyy"
          className={classes.dateFrom}
        />
        {" - "}
        <DatePicker
          selected={toDate}
          onChange={setToDate}
          selectsEnd
          startDate={fromDate}
          endDate={toDate}
          placeholderText={modalText.toDate}
          dateFormat="MM/dd/yyyy"
          className={classes.dateTo}
        />
      </div>
      <ResetFiltersBtn
        text={modalText.history.filters.resetSelectsBtn}
        onClick={resetFilters}
        disabled={isResetButtonDisabled}
      />
    </div>
  );

  return (
    <ModalWrapper
      onClick={handleCloseModal}
      title={displayText}
      style={{ height: "600px", width: "900px" }}
    >
      <BaseTable
        containerWidth={"100%"}
        tableStyle={{ padding: "0", margin: "0" }}
        columns={columns}
        columnAndRowWidth="200px"
        rows={rows}
        status={status}
        items={deliveries}
        errorMsg={modalText.noHistory}
        noItemsMsg={modalText.noHistory}
        filters={filters}
        resultsContainerHeight={isDriverRoutes ? "510px" : "460px"}
      />
    </ModalWrapper>
  );
}
